/**
 * Client-side dictation helpers for the chat composer.
 *
 * The browser records a short clip with MediaRecorder and posts it to the
 * runtime's `POST /web/transcribe` (via the `/gateway/*` rewrite), which runs
 * the same transcription machinery Telegram voice notes use and returns the
 * transcript.
 *
 * What happens to that transcript is the user's choice — see the auto-send
 * preference below. The default is to send it straight to the agent, the way a
 * Telegram voice note works.
 */

import { createBooleanPref } from "./browser-pref";

/** Recording container candidates in preference order. Chrome/Edge/Firefox
 *  produce WebM/Opus; Safari records MP4/AAC; Firefox also supports Ogg/Opus.
 *  All of these are on the transcription API's accepted-container list, so no
 *  transcoding is needed anywhere. */
const RECORDING_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
] as const;

/** The preferred supported recording MIME type, or null when MediaRecorder is
 *  unavailable (SSR, insecure context, unsupported browser). */
export function pickRecordingMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  return RECORDING_MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) ?? null;
}

/** Upload filename for a recorded blob — the transcription API string-matches
 *  the extension, so derive it from the base MIME ("audio/webm;codecs=opus" → "webm"). */
export function dictationFileName(mimeType: string): string {
  const base = mimeType.split(";")[0].trim().toLowerCase();
  const ext = base === "audio/mp4" ? "mp4"
    : base === "audio/ogg" ? "ogg"
    : base === "audio/webm" ? "webm"
    : (base.split("/")[1] || "webm");
  return `dictation.${ext}`;
}

export type TranscribeError =
  | { kind: "budget_depleted" }
  | { kind: "rejected"; status: number; reason: string }
  | { kind: "network"; message: string };

/** Narrow an unknown rejection to the typed {@link TranscribeError} union. */
export const isTranscribeError = (err: unknown): err is TranscribeError =>
  typeof err === "object" && err !== null && "kind" in err;

/**
 * POST a recorded audio blob to the transcribe endpoint. Resolves with the
 * transcript text; rejects with a typed {@link TranscribeError}.
 */
export async function transcribeBlob(url: string, blob: Blob, headers: Record<string, string> = {}): Promise<string> {
  const form = new FormData();
  form.append("audio", blob, dictationFileName(blob.type || "audio/webm"));

  const res = await fetch(url, { method: "POST", body: form, headers }).catch((err: unknown) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- typed TranscribeError union is the documented API; consumers narrow on .kind (the lib/uploads.ts idiom)
    throw { kind: "network", message: err instanceof Error ? err.message : "Network error" } satisfies TranscribeError;
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- typed TranscribeError union (see above)
    throw (res.status === 402
      ? { kind: "budget_depleted" }
      : { kind: "rejected", status: res.status, reason: body.error ?? `HTTP ${res.status}` }) satisfies TranscribeError;
  }

  const data = (await res.json()) as { text?: string };
  return data.text ?? "";
}

/** Dictation continues a draft rather than replacing it, in both modes: an
 *  already-typed draft keeps its place and the transcript is appended after it.
 *  Callers filter out silence, so an empty transcript never reaches here. */
export function appendTranscript(draft: string, transcript: string): string {
  const typed = draft.trimEnd();
  const spoken = transcript.trim();
  return typed ? `${typed} ${spoken}` : spoken;
}

// ── Auto-send preference ────────────────────────────────────────────────────
//
// Dictation used to always drop the transcript into the composer for review.
// Most people don't want that extra step — they speak in order to send — so
// sending immediately is the default and reviewing first is opt-in. Per browser
// rather than per account (there is no column for it), which also means every
// existing user picks up the new default the moment this ships.

const autoSend = createBooleanPref("dictation-auto-send", true);

/** Whether a finished dictation is sent immediately (the default) or inserted
 *  into the composer to review and edit first. */
export const useDictationAutoSendEnabled = autoSend.useValue;
export const setDictationAutoSendEnabled = autoSend.set;
